import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/OrdersService';
import { Order } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useOrder = (orderId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const orderQuery = useQuery({
        queryKey: ['order', orderId],
        queryFn: () => fetch(orderId!),
        enabled: !!orderId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: Order) => {
            const { createdByUsername: _createdByUsername, created: _created, updated: _updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            notifySuccess('Order created');
            queryClient.invalidateQueries({ queryKey: ['orders'] });
        },
        onError: notifyError
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: Order) => {
            if (!orderId) throw new Error('orderId is required parameter');
            const { createdByUsername: _createdByUsername, created: _created, updated: _updated, ...rest } = values;
            return update(orderId, rest);
        },
        onSuccess: () => {
            notifySuccess('Order updated');
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['order', orderId] });
        },
        onError: notifyError
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!orderId) throw new Error('orderId is required parameter');
            return deleteRecord(orderId);
        },
        onSuccess: () => {
            notifySuccess('Order deleted');
            queryClient.invalidateQueries({ queryKey: ['orders'] });
        },
        onError: notifyError
    });

    return {
        record: orderQuery.data,
        loading: orderQuery.isLoading,
        fetchRecord: orderQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};